import noTimeSlot from "@/assets/images/no-shift.png";
import {
  AddWorkScheduleCommandItem,
  UserWorkScheduleAppointmentDto,
  WeekDay,
} from "@/models/club";
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Drawer,
  Group,
  Image,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { IconCirclePlus, IconTrash } from "@tabler/icons-react";
import {
  addHoursToTime,
  AddTimeSlotType,
  generateTimeOptions,
  schema,
  WeekDays,
} from "./type";

// Generate time options from 00:00 to 23:55 in 5-minute intervals
const TIME_OPTIONS = generateTimeOptions();

type Props = {
  opened: boolean;
  close: () => void;
  selectedWeekDay: number;
  selectedTeam: UserWorkScheduleAppointmentDto;
  addTimeSlotToDay: (timeSlots: AddWorkScheduleCommandItem) => void;
};

function AddTimeSlot({
  opened,
  close,
  selectedWeekDay,
  selectedTeam,
  addTimeSlotToDay,
}: Props) {
  const form = useForm<AddTimeSlotType>({
    initialValues: {
      timeSlots: [],
    },
    validate: yupResolver(schema),
  });

  const removeItem = (index: number) => {
    return () => {
      const updatedTimeSlots = form.values.timeSlots.filter(
        (_, i) => i !== index
      );
      form.setFieldValue("timeSlots", updatedTimeSlots);
    };
  };

  const addNewTimeSlot = () => {
    const lastTimeSlot =
      form.values.timeSlots[form.values.timeSlots.length - 1];

    let newTimeSlot;
    if (lastTimeSlot) {
      // New slot starts at the last slot's end time, and ends 1 hour later
      newTimeSlot = {
        startTime: addHoursToTime(lastTimeSlot.endTime, 1),
        endTime: addHoursToTime(lastTimeSlot.endTime, 2),
      };
    } else {
      // Default first time slot
      newTimeSlot = { startTime: "09:00", endTime: "17:00" };
    }

    form.setFieldValue("timeSlots", [...form.values.timeSlots, newTimeSlot]);
  };

  const submit = (values: AddTimeSlotType) => {
    const timeSlots = values.timeSlots.map((timeSlot) => ({
      startTime: {
        hour: parseInt(timeSlot.startTime.split(":")[0]),
        minute: parseInt(timeSlot.startTime.split(":")[1]),
      },
      endTime: {
        hour: parseInt(timeSlot.endTime.split(":")[0]),
        minute: parseInt(timeSlot.endTime.split(":")[1]),
      },
    }));
    addTimeSlotToDay({
      weekDay: selectedWeekDay as WeekDay,
      timeSlots,
    });
    close();
  };

  return (
    <Drawer
      opened={opened}
      onClose={close}
      title={
        <Text>
          شیفت روز "{WeekDays[selectedWeekDay as keyof typeof WeekDays]}" برای "
          {selectedTeam?.user?.firstName}"
        </Text>
      }
      radius="md"
      position="bottom"
      size="lg"
      styles={{
        body: {
          padding: 0,
        },
      }}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: "70svh",
        }}
      >
        <Box style={{ flex: 1 }} px="md" mb="lg">
          {form.values.timeSlots.length === 0 ? (
            <>
              <Stack
                justify="center"
                align="center"
                flex={1}
                h="100%"
                gap="xl"
                mt="100"
              >
                <Image src={noTimeSlot} alt="no time slot" w={60} h={60} />
                <Text>
                  شیفتی برای{" "}
                  {WeekDays[selectedWeekDay as keyof typeof WeekDays]} تعریف
                  نشده است
                </Text>
                <Button
                  variant="default"
                  radius="xl"
                  leftSection={<IconCirclePlus stroke={1} size={20} />}
                  onClick={addNewTimeSlot}
                >
                  افزودن
                </Button>
              </Stack>
            </>
          ) : (
            <form
              id="add-time-slot"
              name="add-time-slot"
              onSubmit={form.onSubmit(submit)}
            >
              <Stack>
                {form.values.timeSlots.map((item, index) => (
                  <Group key={index} align="flex-start" wrap="nowrap">
                    <Select
                      label={index === 0 ? "زمان شروع" : ""}
                      value={item.startTime}
                      size="lg"
                      data={TIME_OPTIONS}
                      onChange={(value) => {
                        form.setFieldValue(
                          `timeSlots.${index}.startTime`,
                          value || ""
                        );
                      }}
                      searchable
                      style={{ flex: 1 }}
                      error={form.errors[`timeSlots.${index}.startTime`]}
                      styles={{
                        error: {
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                    <Select
                      label={index === 0 ? "زمان پایان" : ""}
                      value={item.endTime}
                      data={TIME_OPTIONS}
                      size="lg"
                      onChange={(value) => {
                        form.setFieldValue(
                          `timeSlots.${index}.endTime`,
                          value || ""
                        );
                      }}
                      searchable
                      style={{ flex: 1 }}
                      error={form.errors[`timeSlots.${index}.endTime`]}
                      styles={{
                        error: {
                          fontSize: "0.8rem",
                        },
                      }}
                    />

                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="lg"
                      mt={index === 0 ? "xl" : "xs"}
                      onClick={removeItem(index)}
                    >
                      <IconTrash stroke={1} />
                    </ActionIcon>
                  </Group>
                ))}
                <Button
                  variant="default"
                  radius="xl"
                  w="fit-content"
                  leftSection={<IconCirclePlus stroke={1} size={20} />}
                  onClick={addNewTimeSlot}
                >
                  افزودن تایم
                </Button>
              </Stack>
            </form>
          )}
        </Box>
      </Box>

      <Card
        styles={{
          root: {
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          },
        }}
        withBorder
        radius={0}
      >
        <Stack>
          {form?.errors?.timeSlots && (
            <Text c="red" size="xs">
              {form?.errors?.timeSlots as string}
            </Text>
          )}

          <Button
            type="submit"
            size="lg"
            form="add-time-slot"
            fullWidth

          >
            افزودن
          </Button>
        </Stack>
      </Card>
    </Drawer>
  );
}

export default AddTimeSlot;
